import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import { ago } from './adminUi';

/**
 * Operator alerts panel: node flips, channels with all mirrors dead, and
 * playback-error spikes. Always-on in-dashboard surface (off-platform push is
 * a config flip — see docs/ALERTING.md). Ack silences the unread badge;
 * Resolve closes the alert (and lets a new one of the same kind fire later).
 */
type Alert = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminListAlerts>>['data']>[number];

export default function AlertsPanel() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminListAlerts();
    if (res.success && res.data) setAlerts(res.data);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  const act = async (id: string, fn: (id: string) => Promise<{ success: boolean; error?: { message: string } }>, label: string) => {
    setBusy(id);
    const res = await fn(id);
    if (res.success) { toast.success(label); await refresh(); }
    else toast.error(res.error?.message ?? 'Failed');
    setBusy(null);
  };

  if (alerts.length === 0) return null;
  const sev = (s: string) => s === 'critical' ? 'border-rose-300 bg-rose-50' : s === 'warn' ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50';
  const sevDot = (s: string) => s === 'critical' ? 'bg-rose-500' : s === 'warn' ? 'bg-amber-500' : 'bg-gray-400';

  return (
    <section className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 className="font-semibold">Alerts</h2>
        <span className="text-xs text-gray-500">{alerts.length} open</span>
      </div>
      <div className="divide-y divide-gray-100">
        {alerts.map((a) => (
          <div key={a.id} className={`px-3 py-2 border-l-4 ${sev(a.severity)} flex items-start justify-between gap-3`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`inline-block h-2 w-2 rounded-full ${sevDot(a.severity)}`} />
                <span className="font-medium text-sm">{a.title}</span>
                {!a.acknowledged_at && <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" title="unread" />}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{a.alert_type} · {ago(a.created_at)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!a.acknowledged_at && (
                <button disabled={busy === a.id} onClick={() => void act(a.id, streamFlowApi.adminAckAlert, 'Acknowledged')}
                  className="text-xs text-blue-600 hover:underline disabled:opacity-50">Ack</button>
              )}
              <button disabled={busy === a.id} onClick={() => void act(a.id, streamFlowApi.adminResolveAlert, 'Resolved')}
                className="text-xs text-gray-500 hover:underline disabled:opacity-50">Resolve</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
